import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from './store';
import { APISession, SessionSpec } from "../utils/sessionEndpoints";
import {
  getSessionStorage,
  loadFromStorageKeyPrefix,
  persistSectionPrefixed,
  persistEntryPrefixed,
} from "./reduxStorageCommon"
import { SupportedCodecKey } from '../hooks/useDecoderCapabilities';

export enum UserMode {
  SingleUser = "single",
  Host = "host",
  Guest = "guest",
}

interface SessionState {
  apiSession?: APISession; // YES THIS OBSOLETES MOST OF THE BELOW STUFF but refactoring it is a bigger thing

  // Used only for the final feedback screen. It should survive
  // session-wipe, but not logout.
  feedbackData: {
    conversationId?: string;
    botName?: string;
  };
  connection: {
    websocketUrl?: string;
    baseUrl?: string;
    sessionSpec?: SessionSpec;
    sessionId?: string;
    streamToken?: string;
  };
  conversation: {
    id?: string;
    userIsHost?: boolean;
    mode?: UserMode; // where to put? reorg!
  };
  streaming: {
    pixelStreamerURL?: string;
    tokenMicEnabled?: boolean;
    preferredCodecOverride?: SupportedCodecKey | null;
    videoCodec?: SupportedCodecKey | null;
  };
  user: {
    userId?: string;
  };
}

const SessionPrefix = "session_";

const getStorage = () => {
  return getSessionStorage();
};

const loadFromStorage = (key: string) => {
  return loadFromStorageKeyPrefix(key, SessionPrefix, getStorage());
};

const initialState: SessionState = {
  apiSession: loadFromStorage('apiSession'),
  feedbackData: {
    conversationId: loadFromStorage('feedbackConversationId'),
    botName: loadFromStorage('feedbackBotName'),
  },
  connection: {
    websocketUrl: loadFromStorage('websocketUrl'),
    baseUrl: loadFromStorage('baseUrl'),
    sessionSpec: loadFromStorage('sessionSpec'),
    sessionId: loadFromStorage('sessionId'),
    streamToken: loadFromStorage('streamToken'),
  },
  conversation: {
    id: loadFromStorage('id'),
    userIsHost: loadFromStorage('userIsHost'),
    mode: loadFromStorage('mode') as UserMode | undefined,
  },
  streaming: {
    pixelStreamerURL: loadFromStorage('pixelStreamerURL'),
    tokenMicEnabled: loadFromStorage('tokenMicEnabled'),
    preferredCodecOverride: loadFromStorage('preferredCodecOverride'),
    videoCodec: loadFromStorage('videoCodec'),
  },
  user: {
    userId: loadFromStorage('userId'),
  },
};

export const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setApiSession: (state, action: PayloadAction<APISession | undefined>) => {
      state.apiSession = action.payload;
    },
    setFeedbackData: (state, action: PayloadAction<Partial<SessionState['feedbackData']>>) => {
      state.feedbackData = { ...state.feedbackData, ...action.payload };
    },
    setConnectionData: (state, action: PayloadAction<Partial<SessionState['connection']>>) => {
      state.connection = { ...state.connection, ...action.payload };
    },
    setConversationData: (state, action: PayloadAction<Partial<SessionState['conversation']>>) => {
      state.conversation = { ...state.conversation, ...action.payload };
    },
    setStreamingData: (state, action: PayloadAction<Partial<SessionState['streaming']>>) => {
      state.streaming = { ...state.streaming, ...action.payload };
    },
    setUserData: (state, action: PayloadAction<Partial<SessionState['user']>>) => {
      state.user = { ...state.user, ...action.payload };
    },
    setSessionData: (state, action: PayloadAction<{
      apiSession?: APISession,
      feedbackData?: Partial<SessionState['feedbackData']>,
      connection?: Partial<SessionState['connection']>,
      conversation?: Partial<SessionState['conversation']>,
      streaming?: Partial<SessionState['streaming']>,
      user?: Partial<SessionState['user']>,
    }>) => {
      if (action.payload.apiSession) {
        state.apiSession = { ...state.apiSession, ...action.payload.apiSession };
      }
      if (action.payload.feedbackData) {
        state.feedbackData = { ...state.feedbackData, ...action.payload.feedbackData };
      }
      if (action.payload.connection) {
        state.connection = { ...state.connection, ...action.payload.connection };
      }
      if (action.payload.conversation) {
        state.conversation = { ...state.conversation, ...action.payload.conversation };
      }
      if (action.payload.streaming) {
        state.streaming = { ...state.streaming, ...action.payload.streaming };
      }
      if (action.payload.user) {
        state.user = { ...state.user, ...action.payload.user };
      }
    },
    clearSessionData: (state) => {
      state.apiSession = undefined;
      state.connection = {};
      state.conversation = {};
      state.streaming = {};
      state.user = {};

      // NO, DO NOT
      // state.feedbackData = {};
    }
  }
});

export const {
  setApiSession,
  setConnectionData,
  setConversationData,
  setStreamingData,
  setUserData,
  setSessionData,
  setFeedbackData,
  clearSessionData
} = sessionSlice.actions;

export const selectAPISession = (state: RootState) => state.session.apiSession;
export const selectFeedbackData = (state: RootState) => state.session.feedbackData;
export const selectConversationId = (state: RootState) => state.session.conversation.id;
export const selectWebsocketUrl = (state: RootState) => state.session.connection.websocketUrl;
export const selectPixelStreamerURL = (state: RootState) => state.session.streaming.pixelStreamerURL;
export const selectUserIsHost = (state: RootState) => state.session.conversation.userIsHost;
export const selectUserMode = (state: RootState) => state.session.conversation.mode;
export const selectTokenMicEnabled = (state: RootState) => state.session.streaming.tokenMicEnabled;
export const selectUserId = (state: RootState) => state.session.user.userId;
export const selectBaseUrl = (state: RootState) => state.session.connection.baseUrl;
export const selectConversationSessionSpec = (state: RootState) => state.session.connection.sessionSpec;
export const selectSessionId = (state: RootState) => state.session.connection.sessionId;
export const selectStreamToken = (state: RootState) => state.session.connection.streamToken;

export const selectConnectionData = (state: RootState) => state.session.connection;
export const selectConversationData = (state: RootState) => state.session.conversation;
export const selectStreamingData = (state: RootState) => state.session.streaming;
export const selectUserData = (state: RootState) => state.session.user;
export const selectVideoCodec = (state: RootState) => state.session.streaming.videoCodec;
export const selectPreferredCodecOverride = (state: RootState) => state.session.streaming.preferredCodecOverride;

export const selectHasSession = (state: RootState) => {
  const sessionId = state.session.connection.sessionId;
  const conversationId = state.session.conversation.id;
  return !!(sessionId || conversationId);
};

const persistSection = (section: Record<string, any>) => {
  persistSectionPrefixed(SessionPrefix, section, getSessionStorage());
};

const persistEntry = (key: string, value: any) => {
  persistEntryPrefixed(SessionPrefix, key, value, getSessionStorage());
};

export const setupSessionPersistence = (store: any) => {
  store.subscribe(() => {
    const sessionState = store.getState().session;

    persistEntry('apiSession', sessionState.apiSession);
    persistSection(sessionState.feedbackData);
    persistSection(sessionState.connection);
    persistSection(sessionState.conversation);
    persistSection(sessionState.streaming);
    persistSection(sessionState.user);
  });
};

export default sessionSlice.reducer;
